import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, KeyRound, UserPlus, Trash2, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABEL = {
  property_manager: 'Property Manager (PM-Admin)',
  asset_manager: 'Asset Manager (PM-Admin)',
  maintenance_supervisor: 'Maintenance Supervisor',
  pm_maintenance: 'Maintenance Technician',
};

const PmUsers = () => {
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: 'pm_maintenance', group_ids: [] });
  const [busy, setBusy] = useState(false);
  const [tempPwBanner, setTempPwBanner] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: u }, { data: g }] = await Promise.all([
        axios.get(`${API}/pm/users`, { withCredentials: true }),
        axios.get(`${API}/pm/access-groups`, { withCredentials: true }),
      ]);
      setUsers(u.items || []);
      setGroups(g.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Failed to load users');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/pm/users`, form, { withCredentials: true });
      toast.success('User created');
      setTempPwBanner({ email: data.email, temp_password: data.temp_password });
      setOpen(false);
      setForm({ name: '', email: '', phone: '', role: 'pm_maintenance', group_ids: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Create failed');
    }
    setBusy(false);
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}?`)) return;
    try {
      const { data } = await axios.post(`${API}/pm/users/${u.id}/reset-password`, {}, { withCredentials: true });
      setTempPwBanner({ email: u.email, temp_password: data.temp_password });
      toast.success('Password reset — share the temporary password with the user');
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.put(`${API}/pm/users/${u.id}`, { is_active: !u.is_active }, { withCredentials: true });
      toast.success(u.is_active ? 'Deactivated' : 'Activated');
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const archive = async (u) => {
    if (!window.confirm(`Remove ${u.name}? This archives the user. Super-admin can permanently delete from Trash.`)) return;
    try {
      await axios.delete(`${API}/pm/users/${u.id}`, { withCredentials: true });
      toast.success('User removed');
      load();
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="pm-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2"><Users className="w-6 h-6 text-[#D4AF37]" /> User Management</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Manage Maintenance Technicians and PM-Admin users for your company.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-pm-user-btn" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]">
          <UserPlus className="w-4 h-4 mr-1" /> Add User
        </Button>
      </div>

      {tempPwBanner && (
        <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-md p-3 text-sm" data-testid="temp-password-banner">
          <p className="text-yellow-300 font-medium">Temporary password (share securely with {tempPwBanner.email}):</p>
          <p className="text-white font-mono text-lg mt-1">{tempPwBanner.temp_password}</p>
          <button onClick={() => setTempPwBanner(null)} className="text-[#A3A3A3] text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      <div className="bg-[#141414] border border-[#262626] rounded-md">
        {loading ? <div className="p-6 text-[#A3A3A3]">Loading…</div> : users.length === 0 ? (
          <div className="p-12 text-center text-[#A3A3A3]" data-testid="pm-users-empty">
            <p>No users yet. Add a Maintenance Technician to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#262626]">
            {users.map((u) => (
              <li key={u.id} data-testid={`pm-user-${u.id}`} className="p-4 flex items-start gap-4 hover:bg-[#1a1a1a]">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-medium">{u.name}</p>
                    <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30">{ROLE_LABEL[u.role] || u.role}</Badge>
                    {!u.is_active && <Badge className="bg-red-500/15 text-red-300 border border-red-500/30">Inactive</Badge>}
                  </div>
                  <p className="text-[#A3A3A3] text-sm">{u.email}{u.phone ? ` · ${u.phone}` : ''}</p>
                  {u.last_login_at && <p className="text-[#525252] text-xs mt-1">Last login: {new Date(u.last_login_at).toLocaleString()}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Button size="sm" variant="outline" className="border-[#404040] text-white" onClick={() => resetPassword(u)} data-testid={`reset-pw-${u.id}`}><KeyRound className="w-3 h-3 mr-1" /> Reset password</Button>
                  <Button size="sm" variant="outline" className="border-[#404040] text-white" onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`}><Power className="w-3 h-3 mr-1" /> {u.is_active ? 'Deactivate' : 'Activate'}</Button>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-300 hover:bg-red-500/10" onClick={() => archive(u)} data-testid={`remove-${u.id}`}><Trash2 className="w-3 h-3 mr-1" /> Remove</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">A temporary password will be generated and shown once. Share it with the user via your usual secure channel.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="pm-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="pm-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Phone</Label>
              <Input data-testid="pm-user-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="pm-user-role" className="bg-[#0A0A0A] border-[#262626] text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626] text-white">
                  <SelectItem value="pm_maintenance">Maintenance Technician</SelectItem>
                  <SelectItem value="property_manager">Property Manager (PM-Admin)</SelectItem>
                  <SelectItem value="asset_manager">Asset Manager (PM-Admin)</SelectItem>
                  <SelectItem value="maintenance_supervisor">Maintenance Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {groups.length > 0 && (
              <div>
                <Label>Access Groups <span className="text-[#A3A3A3] text-xs">(optional)</span></Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {groups.map((g) => {
                    const selected = form.group_ids.includes(g.id);
                    return (
                      <button key={g.id} type="button" data-testid={`group-toggle-${g.id}`}
                        onClick={() => setForm({ ...form, group_ids: selected ? form.group_ids.filter((x) => x !== g.id) : [...form.group_ids, g.id] })}
                        className={`text-xs px-3 py-1 rounded border ${selected ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#404040] text-white'}`}>
                        {g.name} <span className="opacity-70">({g.property_count})</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#404040] text-white" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button data-testid="submit-pm-user" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" disabled={busy || !form.name || !form.email} onClick={submit}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PmUsers;
